import { AppModal } from "@/components/ui/AppModal/AppModal";
import { AttorneyFeeSelect } from "@/components/features/matter/AttorneyFeeSelect/AttorneyFeeSelect";
import { MatterContactConnectionModalPreview } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionModalPreview";
import { AddContactFormField } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { useMatterReferralContacts } from "@/hooks/use-matter-referral-contacts";
import { ContactConnectionInFormSchema } from "@/lib/data/schemas/contact/ContactSchema";
import { MatterStatusActionModal } from "@/components/features/matter/MatterStatusActionModal";
import {
  Button,
  CheckboxFormField,
  DateFormField,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  MultiselectFormField,
  SelectFormField,
  Slideover,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MultiCategorySelectFormField } from "@/components/ui/FormFields/MultiCategorySelectFormField/MultiCategorySelectFormField";
import { useCreateMatterStatusPhase } from "@/lib/data/react-query";
import { useUpdateMatter } from "@/lib/data/react-query/matters/matter";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  getMatterStatusCategoryScope,
  getMatterStatusTransitionActions,
  MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS,
} from "@/lib/data/static/matterStatics";
import { MatterSchema } from "@/lib/data/schemas/matters/MatterSchema";
import type { Matter, MatterContactConnection } from "@/lib/types";
import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Trash } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const quickEditFormSchema = MatterSchema.pick({
  attorneyFee: true,
  title: true,
  criticalInfo: true,
  incidentDescription: true,
  dateRetained: true,
  filedDate: true,
  incidentTypeId: true,
  claimTypeIds: true,
}).extend({
  referralContact: ContactConnectionInFormSchema,
  refereeContact: ContactConnectionInFormSchema,
  clientNoSocialSecurity: z.boolean().optional(),
  statusId: z.string().min(1, { message: "Status is required" }),
});

interface MatterDetailsQuickEditSlideoverProps {
  matter: Matter;
  open: boolean;
  setOpen: (open: boolean) => void;
  canUpdate: boolean;
  canChangeStatus: boolean;
  canDelete: boolean;
  onRequestDelete: () => void;
  presentation?: "slideover" | "modal";
}

export const MatterDetailsQuickEditSlideover = ({
  matter,
  open,
  setOpen,
  canUpdate,
  canChangeStatus,
  canDelete,
  onRequestDelete,
  presentation = "slideover",
}: MatterDetailsQuickEditSlideoverProps) => {
  const referralContacts = useMatterReferralContacts(matter);
  const { referral, referee } = referralContacts;
  const MATTER_QUICK_EDIT_FORM_ID = "matter-quick-edit-form";
  const form = useForm<z.infer<typeof quickEditFormSchema>>({
    resolver: zodResolver(quickEditFormSchema),
    // Refetches during a multi-step save must not reset the draft or isSubmitting.
    // The closed-modal effect below refreshes defaults for the next edit.
    defaultValues: {
      attorneyFee: matter.attorneyFee ?? "",
      referralContact: referral?.contact,
      refereeContact: referee?.contact,
      title: matter.title,
      incidentTypeId: matter.incidentTypeId ?? "",
      claimTypeIds: matter.claimTypeIds,
      statusId: matter.statusId,
      criticalInfo: matter.criticalInfo ?? "",
      incidentDescription: matter.incidentDescription ?? "",
      dateRetained: matter.dateRetained ?? "",
      filedDate: matter.filedDate ?? "",
      clientNoSocialSecurity: matter.clientNoSocialSecurity ?? false,
    },
    mode: "onSubmit",
  });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusAction, setStatusAction] = useState<
    "referred" | "closed" | "reopen" | null
  >(null);
  const [statusActionOpen, setStatusActionOpen] = useState(false);
  const isTerminal = ["referred", "closed"].includes(matter.status.categoryId);
  const [selectedStatusLabel, setSelectedStatusLabel] = useState(
    matter.status.label,
  );
  const createMatterPhase = useCreateMatterStatusPhase();
  const updateMatter = useUpdateMatter();
  const updateMatterFee = useUpdateMatter({
    successToast: "Attorney fee updated successfully",
  });
  const {
    data: matterSettingsData,
    isLoading,
    isError,
  } = useSuspenseQuery(retrieveMatterSettingsQueryOptions());
  const incidentTypes = (matterSettingsData.data.incidentTypes ?? []).map(
    (type) => ({
      id: type.id,
      label: getIncidentTypeLabel(type),
    }),
  );
  if (
    matter.incidentTypeId &&
    !incidentTypes.some((type) => type.id === matter.incidentTypeId)
  ) {
    incidentTypes.push({
      id: matter.incidentTypeId,
      label: getIncidentTypeLabel(
        matter.incidentType ?? { id: matter.incidentTypeId },
      ),
    });
  }
  const claimTypeOptions = (matterSettingsData.data.claimTypes ?? []).map(
    (type) => ({
      value: type.id,
      label: type.label,
    }),
  );
  const matterClaimTypes = matter.claimTypes ?? [];
  for (const claimTypeId of matter.claimTypeIds) {
    if (!claimTypeOptions.some((type) => type.value === claimTypeId)) {
      claimTypeOptions.push({
        value: claimTypeId,
        label:
          matterClaimTypes.find((type) => type.id === claimTypeId)?.label ??
          `Unavailable claim type (${claimTypeId})`,
      });
    }
  }
  const selectedIncidentTypeId = form.watch("incidentTypeId") || null;
  const selectedClaimTypeIds = form.watch("claimTypeIds");
  const hasIncidentTypeChanged =
    selectedIncidentTypeId !== (matter.incidentTypeId ?? null);
  const hasClaimTypesChanged =
    selectedClaimTypeIds.length !== matter.claimTypeIds.length ||
    selectedClaimTypeIds.some((id) => !matter.claimTypeIds.includes(id));

  const matterStatuses = useMemo(() => {
    const allowedCategories = getMatterStatusCategoryScope(
      matter.status.categoryId,
    );
    return matterSettingsData.data.statuses
      .filter((status) =>
        isTerminal
          ? status.categoryId === matter.status.categoryId
          : status.categoryId !== "referred" &&
            status.categoryId !== "closed" &&
            allowedCategories.includes(
              status.categoryId as (typeof allowedCategories)[number],
            ),
      )
      .reduce<
        {
          category: string;
          items: { value: string; label: string }[];
        }[]
      >((acc, status) => {
        const category = status.categoryId;
        const item = {
          value: status.id,
          label: status.label,
        };
        const existingCategory = acc.find((c) => c.category === category);
        if (existingCategory) {
          existingCategory.items.push(item);
        } else {
          acc.push({ category, items: [item] });
        }

        return acc;
      }, []);
  }, [matter.status.categoryId, matterSettingsData.data.statuses, isTerminal]);

  const handleStatusChange = (value: string) => {
    const selectedLabel =
      matterSettingsData.data.statuses.find((status) => status.id === value)
        ?.label ?? matter.status.label;
    setSelectedStatusLabel(selectedLabel);
    form.setValue("statusId", value, { shouldDirty: true });
    setStatusDropdownOpen(false);
  };

  const handleSubmit = async (data: z.infer<typeof quickEditFormSchema>) => {
    if (!canUpdate) {
      return;
    }
    const dirtyFields = form.formState.dirtyFields;
    const normalizedNoSsn = Boolean(data.clientNoSocialSecurity);
    const previousNoSsn = Boolean(matter.clientNoSocialSecurity);
    const currentAttorneyFee =
      matter.attorneyFee == null || matter.attorneyFee === ""
        ? null
        : Number(matter.attorneyFee);
    const submittedAttorneyFee =
      data.attorneyFee == null || data.attorneyFee === ""
        ? null
        : Number(data.attorneyFee);
    const hasAttorneyFeeChanged =
      Boolean(dirtyFields.attorneyFee) &&
      submittedAttorneyFee !== currentAttorneyFee;
    const currentStatusId = matter.statusId;
    const hasStatusChanged =
      canChangeStatus &&
      dirtyFields.statusId &&
      data.statusId !== currentStatusId;
    const matterUpdates = {
      ...(dirtyFields.incidentTypeId &&
        hasIncidentTypeChanged &&
        data.incidentTypeId && { incidentTypeId: data.incidentTypeId }),
      ...(dirtyFields.claimTypeIds &&
        hasClaimTypesChanged && { claimTypeIds: data.claimTypeIds }),
      ...(dirtyFields.title &&
        data.title !== matter.title && { title: data.title }),
      ...(dirtyFields.criticalInfo &&
        (data.criticalInfo ?? "") !== (matter.criticalInfo ?? "") && {
          criticalInfo: data.criticalInfo,
        }),
      ...(dirtyFields.incidentDescription &&
        (data.incidentDescription ?? "") !==
          (matter.incidentDescription ?? "") && {
          incidentDescription: data.incidentDescription,
        }),
      ...(dirtyFields.dateRetained &&
        (data.dateRetained ?? "") !== (matter.dateRetained ?? "") && {
          dateRetained: data.dateRetained ?? "",
        }),
      ...(dirtyFields.filedDate &&
        (data.filedDate ?? "") !== (matter.filedDate ?? "") && {
          filedDate: data.filedDate ?? "",
        }),
      ...(dirtyFields.clientNoSocialSecurity &&
        normalizedNoSsn !== previousNoSsn && {
          clientNoSocialSecurity: normalizedNoSsn,
        }),
      ...(dirtyFields.attorneyFee &&
        hasAttorneyFeeChanged &&
        submittedAttorneyFee !== null && { attorneyFee: submittedAttorneyFee }),
    };
    if (Object.keys(matterUpdates).length > 0) {
      await updateMatter.mutateAsync({
        ...matterUpdates,
        dateModified: matter.dateModified,
        id: matter.id,
      });
    }
    await referralContacts.save({
      referralContact: dirtyFields.referralContact
        ? data.referralContact
        : referral?.contact,
      refereeContact: dirtyFields.refereeContact
        ? data.refereeContact
        : referee?.contact,
    });
    if (hasStatusChanged) {
      const selectedStatus = matterSettingsData.data.statuses.find(
        (status) => status.id === data.statusId,
      );
      const res = await createMatterPhase.mutateAsync({
        dateTimeStart: new Date().toISOString(),
        matterId: matter.id,
        statusId: data.statusId,
      });
      const defaultFee =
        selectedStatus?.defaultAttorneyFee ??
        res.data.defaultAttorneyFee ??
        null;
      if (
        !hasAttorneyFeeChanged &&
        defaultFee !== null &&
        currentAttorneyFee !== Number(defaultFee)
      ) {
        updateMatterFee.mutate({
          attorneyFee: defaultFee,
          dateModified: res.data.dateModified,
          id: matter.id,
        });
      }
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      setStatusActionOpen(false);
      setStatusAction(null);
      form.reset({
        attorneyFee: matter.attorneyFee ?? "",
        referralContact: referral?.contact,
        refereeContact: referee?.contact,
        title: matter.title,
        incidentTypeId: matter.incidentTypeId ?? "",
        claimTypeIds: matter.claimTypeIds,
        statusId: matter.statusId,
        criticalInfo: matter.criticalInfo ?? "",
        incidentDescription: matter.incidentDescription ?? "",
        dateRetained: matter.dateRetained ?? "",
        filedDate: matter.filedDate ?? "",
        clientNoSocialSecurity: matter.clientNoSocialSecurity ?? false,
      });
      setSelectedStatusLabel(matter.status.label);
    }
  }, [
    form,
    matter.id,
    matter.status.label,
    matter.status.id,
    matter.statusId,
    matter.title,
    matter.attorneyFee,
    matter.incidentTypeId,
    matter.claimTypeIds,
    matter.criticalInfo,
    matter.incidentDescription,
    matter.dateRetained,
    matter.filedDate,
    matter.clientNoSocialSecurity,
    open,
    referral?.contact,
    referee?.contact,
  ]);

  const hasTitleChanged = Boolean(form.formState.dirtyFields.title);
  const watchedAttorneyFee = form.watch("attorneyFee");
  const normalizedWatchedAttorneyFee =
    watchedAttorneyFee == null || watchedAttorneyFee === ""
      ? null
      : Number(watchedAttorneyFee);
  const normalizedMatterAttorneyFee =
    matter.attorneyFee == null || matter.attorneyFee === ""
      ? null
      : Number(matter.attorneyFee);
  const hasAttorneyFeeChanged =
    Boolean(form.formState.dirtyFields.attorneyFee) &&
    normalizedWatchedAttorneyFee !== normalizedMatterAttorneyFee;
  const hasStatusChanged = form.watch("statusId") !== matter.statusId;
  const hasNoSsnChanged =
    Boolean(form.watch("clientNoSocialSecurity")) !==
    Boolean(matter.clientNoSocialSecurity);
  const hasCriticalInfoChanged =
    (form.watch("criticalInfo") ?? "") !== (matter.criticalInfo ?? "");
  const hasIncidentDescriptionChanged =
    (form.watch("incidentDescription") ?? "") !==
    (matter.incidentDescription ?? "");
  const hasDateRetainedChanged =
    (form.watch("dateRetained") ?? "") !== (matter.dateRetained ?? "");
  const hasFiledDateChanged =
    (form.watch("filedDate") ?? "") !== (matter.filedDate ?? "");
  const hasReferralChanged =
    (form.watch("referralContact")?.id ?? "") !== (referral?.contactId ?? "");
  const hasRefereeChanged =
    (form.watch("refereeContact")?.id ?? "") !== (referee?.contactId ?? "");
  const isFormLoading =
    form.formState.isSubmitting ||
    updateMatter.isPending ||
    createMatterPhase.isPending;
  const isSaveDisabled =
    !form.formState.isDirty ||
    (!hasTitleChanged &&
      !hasAttorneyFeeChanged &&
      !hasIncidentTypeChanged &&
      !hasClaimTypesChanged &&
      !hasStatusChanged &&
      !hasNoSsnChanged &&
      !hasCriticalInfoChanged &&
      !hasIncidentDescriptionChanged &&
      !hasDateRetainedChanged &&
      !hasFiledDateChanged &&
      !hasReferralChanged &&
      !hasRefereeChanged) ||
    isFormLoading ||
    !canUpdate;

  const connectionFields = (
    <fieldset className="flex min-w-0 flex-col gap-4" disabled={!canUpdate}>
      <AddContactFormField
        register={form.register("referralContact")}
        label="Referred by"
        selectContactLabel="Referred by"
        defaultSearchObject={{ organizationSubTypes: ["Law Firm"] }}
        defaultValues={{ type: "organization" }}
        contactSelectorSize="compact"
        hideCompactContactChrome
        renderSelectedContactPreview={(contact) => (
          <MatterContactConnectionModalPreview
            contactId={contact.id}
            previewContact={contact}
            matterId={matter.id}
            matterContactConnection={
              referral?.contactId === contact.id
                ? { ...referral, label: "Referred by" }
                : ({
                    contact,
                    contactId: contact.id,
                    label: "Referred by",
                  } as MatterContactConnection)
            }
            onRemoveContact={() => {
              form.setValue("referralContact", undefined, {
                shouldDirty: true,
              });
            }}
          />
        )}
        matterId={matter.id}
        onDelete={() => {
          form.setValue("referralContact", undefined, { shouldDirty: true });
        }}
      />
      <AddContactFormField
        register={form.register("refereeContact")}
        label="Referred to"
        selectContactLabel="Referred to"
        defaultSearchObject={{ organizationSubTypes: ["Law Firm"] }}
        defaultValues={{ type: "organization" }}
        contactSelectorSize="compact"
        hideCompactContactChrome
        renderSelectedContactPreview={(contact) => (
          <MatterContactConnectionModalPreview
            contactId={contact.id}
            previewContact={contact}
            matterId={matter.id}
            matterContactConnection={
              referee?.contactId === contact.id
                ? { ...referee, label: "Referred to" }
                : ({
                    contact,
                    contactId: contact.id,
                    label: "Referred to",
                  } as MatterContactConnection)
            }
            onRemoveContact={() => {
              form.setValue("refereeContact", undefined, { shouldDirty: true });
            }}
          />
        )}
        matterId={matter.id}
        onDelete={() => {
          form.setValue("refereeContact", undefined, { shouldDirty: true });
        }}
      />
    </fieldset>
  );

  const formFields = (
    <fieldset
      className={
        presentation === "modal"
          ? "grid grid-cols-1 gap-4 sm:grid-cols-2"
          : slideoverFieldsetClassName
      }
      disabled={!canUpdate}
    >
      <TextFormField label="Case title" register={form.register("title")} />
      <MultiCategorySelectFormField
        label="Matter status"
        buttonClassName="w-full"
        register={form.register("statusId")}
        items={matterStatuses}
        selectedLabel={selectedStatusLabel}
        actions={getMatterStatusTransitionActions(matter.status.categoryId).map(
          ({ action, label }) => ({
            label,
            disabled: !canUpdate || !canChangeStatus || isFormLoading,
            onSelect: () => {
              setStatusAction(action);
              setStatusActionOpen(true);
            },
          }),
        )}
        placeholder="Select status..."
        searchPlaceholder="Search statuses..."
        emptyMessage="No status found."
        open={canUpdate && canChangeStatus ? statusDropdownOpen : false}
        setOpen={
          canUpdate && canChangeStatus
            ? setStatusDropdownOpen
            : () => {
                setStatusDropdownOpen(false);
              }
        }
        isItemsError={isError}
        isItemsLoading={isLoading}
        onChange={handleStatusChange}
        disabled={!canUpdate || !canChangeStatus}
      />
      <FormField
        control={form.control}
        name="attorneyFee"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>Attorney fee (%)</FormLabel>
              <FormMessage />
            </div>
            <AttorneyFeeSelect
              placeholder="Select or enter an attorney fee"
              value={field.value?.toString() ?? ""}
              onChange={(value) => {
                field.onChange(Number(value));
              }}
              options={MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS}
              createMessage="Add an attorney fee"
              emptyMessage="No attorney fee found"
              label=""
              ariaLabel="Attorney Fee (%)"
            />
          </FormItem>
        )}
      />
      <SelectFormField
        label="Incident type"
        placeholder="Unclassified"
        register={form.register("incidentTypeId")}
        items={incidentTypes}
        itemsKey="id"
        formatItemLabel={(item) => item?.label}
        isItemsLoading={isLoading}
        isItemsError={isError}
      />
      <MultiselectFormField
        label="Claim types"
        register={form.register("claimTypeIds")}
        options={claimTypeOptions}
        disabled={!canUpdate || isError}
      />
      <DateFormField
        register={form.register("dateRetained")}
        label="Date retained"
        name="dateRetained"
      />
      <DateFormField
        register={form.register("filedDate")}
        label="Date filed"
        name="filedDate"
      />
      <div className="col-span-1">
        <CheckboxFormField
          label="No client SSN"
          register={form.register("clientNoSocialSecurity")}
        />
      </div>
      <TextAreaFormField
        label="Critical matter information"
        register={form.register("criticalInfo")}
        className="col-span-full"
      />
    </fieldset>
  );

  const formFooter = (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap gap-2">
        {canDelete && (
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              onRequestDelete();
              setOpen(false);
            }}
          >
            <Trash className="mr-2 h-4 w-4" />
            Delete matter
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <FormCancelButton setOpen={setOpen} isDirty={form.formState.isDirty} />
        <FormSubmitButton
          title="Save"
          loading={isFormLoading}
          submitFormId={
            presentation === "modal" ? MATTER_QUICK_EDIT_FORM_ID : undefined
          }
          disabled={isSaveDisabled}
          overrideDisabled
        />
      </div>
    </div>
  );

  const slideoverFormBody = (
    <Form {...form}>
      <form
        className="relative flex grow flex-col overflow-hidden"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        {isFormLoading && <FormLoadingOverlay />}
        <div className="overflow-y-auto">
          <div className="px-6 pt-6">{connectionFields}</div>
          {formFields}
        </div>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t p-6">
          {formFooter}
        </div>
      </form>
    </Form>
  );

  return (
    <>
      {presentation === "modal" ? (
        <Form {...form}>
          <AppModal
            open={open}
            onOpenChange={setOpen}
            isDirty={form.formState.isDirty}
            title="Edit matter details"
            description="Update the matter title, status, attorney fee, incident and claim types, retained/filed dates, or critical information."
            loading={isFormLoading}
            size="4xl"
            preview={connectionFields}
            previewLabel={null}
            bodyFormId={MATTER_QUICK_EDIT_FORM_ID}
            bodyFormOnSubmit={async (event) => {
              event.preventDefault();
              event.stopPropagation();
              await form.handleSubmit(handleSubmit)();
            }}
            footer={formFooter}
          >
            {formFields}
          </AppModal>
        </Form>
      ) : (
        <Slideover
          open={open}
          setOpen={setOpen}
          isDirty={form.formState.isDirty}
          title="Edit matter details"
          description="Update the matter title, status, attorney fee, incident and claim types, retained/filed dates, or critical information."
          isLoading={isFormLoading}
        >
          {slideoverFormBody}
        </Slideover>
      )}
      {statusAction && (
        <MatterStatusActionModal
          key={statusAction}
          action={statusAction}
          open={open && statusActionOpen}
          overlayClassName="z-[108]"
          className="z-[109]"
          submitLabel="Use status"
          onClosed={() => {
            setStatusAction(null);
          }}
          statuses={matterSettingsData.data.statuses
            .filter((status) =>
              statusAction === "reopen"
                ? ["pre-litigation", "litigation"].includes(status.categoryId)
                : status.categoryId === statusAction,
            )
            .sort(
              (a, b) =>
                (a.order ?? Number.MAX_SAFE_INTEGER) -
                  (b.order ?? Number.MAX_SAFE_INTEGER) ||
                a.label.localeCompare(b.label),
            )}
          busy={isFormLoading || !canUpdate || !canChangeStatus}
          onClose={() => {
            setStatusActionOpen(false);
          }}
          onSubmit={(statusId) => {
            if (canUpdate && canChangeStatus && !isFormLoading) {
              handleStatusChange(statusId);
              setStatusActionOpen(false);
            }
            return Promise.resolve();
          }}
        />
      )}
    </>
  );
};
